import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import MailLine from '@/assets/icons/mail_line.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import { Spinner } from '@/components/atoms/Spinner';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import TextField from '@/components/atoms/TextField';

import type { LoginValues } from '../useLoginFlow';

import publicStyles from '../../public.module.scss';
import styles from '../Login.module.scss';

interface LoginEmailStepProps {
    values: LoginValues;
    setValues: (values: LoginValues) => void;
    emailError: string | null;
    setEmailError: (error: string | null) => void;
    checkingEmail: boolean;
    onSubmit: (e: FormEvent) => Promise<void>;
}

export default function LoginEmailStep(props: LoginEmailStepProps) {
    const { values, setValues, emailError, setEmailError, checkingEmail, onSubmit } = props;
    const { t } = useTranslation();

    return (
        <form onSubmit={onSubmit}>
            <FormField
                label={t('publicPages.common.email')}
                labelClassName={publicStyles.inverseLabel}>
                <TextField
                    prefix={<SvgIcon icon={MailLine} />}
                    groupClassName={publicStyles.inputGroup}
                    adornmentClassName={publicStyles.inputGroupText}
                    type="email"
                    name="email"
                    placeholder={t('publicPages.common.email')}
                    value={values.email}
                    className={publicStyles.formControl}
                    onChange={(e) => {
                        setValues({
                            ...values,
                            email: e.target.value,
                        });
                        if (emailError) setEmailError(null);
                    }}
                    disabled={checkingEmail}
                />
            </FormField>
            {emailError && (
                <Alert tone="danger" className={styles.stepMessage}>
                    {emailError}
                </Alert>
            )}
            <div className={classNames(publicStyles.actions, styles.actionsEnd)}>
                {checkingEmail ? (
                    <div className={styles.submitting}>
                        <Spinner size="lg" fullScreen className={styles.loader} />
                    </div>
                ) : (
                    <Button size={'large'} type="submit" disabled={!values.email}>
                        {t('publicPages.login.continue')}
                    </Button>
                )}
            </div>
        </form>
    );
}
